import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../translations';
import LanguageSwitcher from './LanguageSwitcher';
import './Navbar.css';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = getTranslation(language);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      
      // Detect active section
      if (location.pathname === '/') {
        const sections = ['capabilities', 'solutions', 'platform', 'contact'];
        const scrollPosition = window.scrollY + 150;
        
        for (const sectionId of sections) {
          const element = document.getElementById(sectionId);
          if (element) {
            const { offsetTop, offsetHeight } = element;
            if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
              setActiveSection(sectionId);
              break;
            }
          }
        }
      } else if (location.pathname === '/about') {
        setActiveSection('about');
      }
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location]);

  const handleNavClick = (e, sectionId) => {
    e.preventDefault();
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleContactClick = (e) => {
    e.preventDefault();
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
      <div className="navbar-container">
        <a href="/" className="navbar-logo">
          <img src="/logo.png" alt="Agentecfusion" className="logo-image" />
          <span className="logo-text">Agentecfusion</span>
        </a>

        <div className={`navbar-menu ${mobileMenuOpen ? 'open' : ''}`}>
          <a 
            href="/#capabilities" 
            className={`nav-link ${activeSection === 'capabilities' ? 'active' : ''}`}
            onClick={(e) => handleNavClick(e, 'capabilities')}
          >
            {activeSection === 'capabilities' && (
              <motion.div 
                className="nav-link-bg" 
                layoutId="navBg"
                transition={{ type: "spring", stiffness: 380, damping: 30 }}
              />
            )}
            <span className="nav-link-text">{t.nav.capabilities}</span>
          </a>
          <a 
            href="/#solutions" 
            className={`nav-link ${activeSection === 'solutions' ? 'active' : ''}`}
            onClick={(e) => handleNavClick(e, 'solutions')}
          >
            {activeSection === 'solutions' && (
              <motion.div 
                className="nav-link-bg" 
                layoutId="navBg"
                transition={{ type: "spring", stiffness: 380, damping: 30 }}
              />
            )}
            <span className="nav-link-text">{t.nav.applications}</span>
          </a>
          <a 
            href="/#platform" 
            className={`nav-link ${activeSection === 'platform' ? 'active' : ''}`}
            onClick={(e) => handleNavClick(e, 'platform')}
          >
            {activeSection === 'platform' && (
              <motion.div 
                className="nav-link-bg" 
                layoutId="navBg"
                transition={{ type: "spring", stiffness: 380, damping: 30 }}
              />
            )}
            <span className="nav-link-text">{t.nav.platform}</span>
          </a>
          <a 
            href="#contact" 
            className={`nav-link ${activeSection === 'contact' ? 'active' : ''}`}
            onClick={handleContactClick}
          >
            {activeSection === 'contact' && (
              <motion.div 
                className="nav-link-bg" 
                layoutId="navBg"
                transition={{ type: "spring", stiffness: 380, damping: 30 }}
              />
            )}
            <span className="nav-link-text">{t.nav.contact}</span>
          </a>
          <a 
            href="/about" 
            className={`nav-link ${activeSection === 'about' ? 'active' : ''}`}
          >
            {activeSection === 'about' && (
              <motion.div 
                className="nav-link-bg" 
                layoutId="navBg"
                transition={{ type: "spring", stiffness: 380, damping: 30 }}
              />
            )}
            <span className="nav-link-text">{t.nav.aboutUs}</span>
          </a>
          <a 
            href="/safeclaw" 
            className={`nav-link ${location.pathname === '/safeclaw' ? 'active' : ''}`}
          >
            {location.pathname === '/safeclaw' && (
              <motion.div 
                className="nav-link-bg" 
                layoutId="navBg"
                transition={{ type: "spring", stiffness: 380, damping: 30 }}
              />
            )}
            <span className="nav-link-text">
              SafeClaw
              <span className="nav-new-badge">NEW</span>
            </span>
          </a>
        </div>

        <div className="navbar-actions">
          <LanguageSwitcher />
          <motion.a
            href="#contact"
            className="btn-nav"
            onClick={handleContactClick}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {t.nav.requestAccess}
          </motion.a>
        </div>

        <button
          className="mobile-menu-toggle"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>
    </nav>
  );
};

export default Navbar;
